/**
 * Hub view — index of all hubs (route `#hub`), or a single hub's surfaces +
 * entities (route `#hub/<id>`).
 *
 * Phase-0 manifests rarely carry hub tables (hubs live in docs 04/05, parsed in
 * later phases), so Phase 1 renders whatever is present and an honest empty
 * state otherwise.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { Icon } from '../components/Icon.js';
import { EmptyState } from '../components/EmptyState.js';
import { navigate, navigateToObject, navigateToObjectKind } from '../state/hashRouter.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { useAppStore } from '../state/store.js';
import { WorkflowTrail } from './WorkflowTrail.js';

export interface HubViewProps {
  manifest: AtlasManifest;
  hubId?: string;
}

export function HubView({ manifest, hubId }: HubViewProps): React.JSX.Element {
  const { entityTrail } = useAppStore();

  if (!hubId) {
    if (manifest.hubs.length === 0) {
      return (
        <EmptyState
          icon="dashboard"
          title="No hubs in the manifest"
          detail="Hub catalogues land when docs 04/05 are parsed in a later phase."
        />
      );
    }
    return (
      <article>
        <div className="entity-head">
          <Icon name="dashboard" size="lg" />
          <h1 className="entity-title">Hubs</h1>
        </div>
        <div className="section-label">{manifest.hubs.length} hub{manifest.hubs.length === 1 ? '' : 's'}</div>
        {manifest.hubs.map((hub) => (
          <button
            key={hub.id}
            className="ref-row"
            onClick={() => navigate({ kind: 'hub', id: hub.id })}
          >
            <Icon name="dashboard" size="sm" />
            <span>{hub.name}</span>
            <span className="tree-row-count">
              {hub.entityIds.length} entities · {hub.surfaceIds.length} surfaces
            </span>
          </button>
        ))}
      </article>
    );
  }

  const hub = manifest.hubs.find((h) => h.id === hubId);
  if (!hub) {
    return (
      <EmptyState
        icon="dashboard"
        title={`No hub “${hubId}” in the manifest`}
        detail="Hub catalogues land when docs 04/05 are parsed in a later phase."
      >
        <button className="btn btn-ghost" onClick={() => navigate({ kind: 'hub' })}>
          Back to hubs
        </button>
      </EmptyState>
    );
  }

  return (
    <article>
      <div className="entity-head">
        <Icon name="dashboard" size="lg" />
        <h1 className="entity-title">{hub.name}</h1>
      </div>
      <div className="entity-path">{hub.docRef}</div>

      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}

      <div className="section-label">Entities · {hub.entityIds.length}</div>
      {hub.entityIds.map((id) => (
        <button key={id} className="ref-row" onClick={() => navigate({ kind: 'entity', id })}>
          <Icon name="description" size="sm" />
          <span className="mono">{id}</span>
        </button>
      ))}

      <div className="section-label" style={{ marginTop: 'var(--sp-lg)' }}>
        Surfaces · {hub.surfaceIds.length}
      </div>
      {hub.surfaceIds.map((id) => (
        <button key={id} className="ref-row" onClick={() => navigate({ kind: 'surface', id })}>
          <Icon name="credit_card" size="sm" />
          <span className="mono">{id}</span>
        </button>
      ))}

      <RelationshipsPanel manifest={manifest} kind="hub" id={hub.id} onNavigate={navigateToObjectKind} />
    </article>
  );
}
